var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { useScreenSize } from '../../hooks/useScreenSize';
import { Button } from '../Button';
import { Tooltip } from '../Tooltip';
import { SidebarSection } from './SidebarSection';
export const Sidebar = (_a) => {
    var { el: Element = 'div', className, localStorageKey, children } = _a, rest = __rest(_a, ["el", "className", "localStorageKey", "children"]);
    const sidebarRef = useRef();
    const getInitialOpen = () => {
        const wasPreviouslyOpen = typeof window !== 'undefined' &&
            window.localStorage.getItem(localStorageKey);
        return wasPreviouslyOpen ? JSON.parse(wasPreviouslyOpen) : true;
    };
    const screensize = useScreenSize();
    const isDisplaySmall = screensize.smallerThan.desktop;
    const [isHover, setIsHover] = useState(false);
    const [isTempOpen, setIsTempOpen] = useState(false);
    const [isOpen, setIsOpen] = useState(getInitialOpen());
    const hoverTimeout = useRef();
    const isTouchScreen = typeof window !== 'undefined' &&
        window.matchMedia('(pointer:coarse)').matches;
    const SidebarClasses = classnames('Sidebar', className, {
        'Sidebar--collapsed': !isOpen || isDisplaySmall,
        'Sidebar--temporary': isTempOpen,
        'Sidebar--touch': isTouchScreen,
    });
    const toggleButtonClasses = classnames('Sidebar__toggle-button', {
        'Sidebar__toggle-button-visible': isTouchScreen || !isOpen || isDisplaySmall,
    });
    const iconName = isDisplaySmall && isTempOpen
        ? 'keyboard_arrow_left'
        : isDisplaySmall && !isTempOpen
            ? 'keyboard_arrow_right'
            : isOpen
                ? 'keyboard_arrow_left'
                : 'keyboard_arrow_right';
    const size = isTouchScreen ? 'medium' : 'small';
    const [tooltipText, setTooltipText] = useState();
    useEffect(() => {
        const getTooltipText = () => {
            if (isTempOpen) {
                if (!isDisplaySmall)
                    return 'Keep sidebar open';
                if (isDisplaySmall)
                    return 'Close sidebar';
            }
            if (!isOpen || isDisplaySmall)
                return 'Open sidebar';
            return 'Close sidebar';
        };
        setTooltipText(getTooltipText());
    }, [isDisplaySmall, isHover, isOpen, isTempOpen]);
    const handleHover = (state, delay = 300) => {
        clearTimeout(hoverTimeout.current);
        if (!isOpen || isDisplaySmall) {
            setIsHover(state);
            hoverTimeout.current = setTimeout(() => {
                setIsTempOpen(state);
            }, delay);
        }
    };
    const open = () => {
        if (!isDisplaySmall) {
            handleHover(false, 0);
            setIsOpen(!isOpen);
            return;
        }
        setIsTempOpen(!isTempOpen);
    };
    useEffect(() => {
        localStorage.setItem(localStorageKey, JSON.stringify(isOpen));
    }, [localStorageKey, isOpen]);
    return (React.createElement(Element, Object.assign({ className: SidebarClasses, ref: sidebarRef, "data-anvil-component": "Sidebar" }, rest),
        React.createElement("div", { className: "Sidebar__wrapper" },
            React.createElement(Tooltip, { hoverOnly: true, className: "Sidebar__toggle-button-wrapper", direction: "r", text: tooltipText },
                React.createElement(Button, { className: toggleButtonClasses, onClick: open, iconName: iconName, size: size, onMouseOver: () => clearTimeout(hoverTimeout.current), onMouseLeave: () => isTempOpen && handleHover(false), "aria-label": `${isOpen ? 'collapse' : 'expand'} sidebar` })),
            React.createElement("div", { className: "Sidebar__content-wrapper", onMouseOver: () => handleHover(true), onMouseLeave: () => handleHover(false) },
                React.createElement("div", { className: "Sidebar__content" }, children)))));
};
Sidebar.Section = SidebarSection;
//# sourceMappingURL=Sidebar.js.map